// A tiny translator: flat keys, {name} placeholders, plural keys ending in
// _one/_other chosen by Intl.PluralRules. English is bundled; other locales
// load on demand so the vote page stays small (BACKLOG #22).
import en from "./locales/en.json";

export type Dict = Record<string, string>;
export type Locale = "en" | "es" | "pt-BR" | "de" | "fr" | "hi" | "th" | "ko" | "id" | "vi" | "ms" | "ru" | "uk" | "ar" | "he";
export const LOCALES: Locale[] = ["en", "es", "pt-BR", "de", "fr", "hi", "th", "ko", "id", "vi", "ms", "ru", "uk", "ar", "he"];
/** Right-to-left locales: the document gets dir="rtl". */
export const RTL_LOCALES: readonly Locale[] = ["ar", "he"];

let current: Locale = "en";
let dict: Dict = en;
let plural = new Intl.PluralRules("en");
/** The loaded locale file (empty for English), kept so string packs can be merged in later. */
let loaded: Dict = {};
/** English plus every string pack added so far: the fallback for a missing key. */
let fallback: Dict = en;
const packs: Partial<Record<Locale, Dict>>[] = [];

function rebuild(): void {
  fallback = Object.assign({}, en, ...packs.map((p) => p.en ?? {}));
  dict = current === "en" ? fallback : Object.assign({}, fallback, loaded, ...packs.map((p) => p[current] ?? {}));
}

/**
 * Strings that only one lazy screen uses (the admin dashboard, the share kit on the poll's
 * settings page) live in a pack under locales/packs/ and arrive with that screen's chunk, so
 * the vote page never downloads them (its 50 KB budget, Phase 2 gate).
 */
export function addStrings(pack: Partial<Record<Locale, Dict>>): void {
  packs.push(pack);
  rebuild();
}

const loaders: Record<Exclude<Locale, "en">, () => Promise<{ default: Dict }>> = {
  es: () => import("./locales/es.json"),
  "pt-BR": () => import("./locales/pt-BR.json"),
  de: () => import("./locales/de.json"),
  fr: () => import("./locales/fr.json"),
  hi: () => import("./locales/hi.json"),
  th: () => import("./locales/th.json"),
  ko: () => import("./locales/ko.json"),
  id: () => import("./locales/id.json"),
  vi: () => import("./locales/vi.json"),
  ms: () => import("./locales/ms.json"),
  ru: () => import("./locales/ru.json"),
  uk: () => import("./locales/uk.json"),
  ar: () => import("./locales/ar.json"),
  he: () => import("./locales/he.json"),
};

/** Picks the best supported locale from the browser's list. */
export function matchLocale(prefs: readonly string[]): Locale {
  for (const p of prefs) {
    const l = p.toLowerCase();
    if (l.startsWith("pt")) return "pt-BR";
    if (l === "iw" || l.startsWith("iw-")) return "he"; // the old code some Android builds still send
    if (l === "in" || l.startsWith("in-")) return "id";
    for (const c of LOCALES) if (l === c.toLowerCase() || l.split("-")[0] === c) return c;
  }
  return "en";
}

export async function setLocale(locale: Locale): Promise<void> {
  loaded = locale === "en" ? {} : (await loaders[locale]()).default;
  current = locale;
  rebuild();
  plural = new Intl.PluralRules(locale);
  document.documentElement.lang = locale;
  document.documentElement.dir = RTL_LOCALES.includes(locale) ? "rtl" : "ltr";
}

export function locale(): Locale {
  return current;
}

/** Translate `key`, filling {name} placeholders. With `count`, picks the plural form. */
export function t(key: string, vars: Record<string, string | number> = {}): string {
  let k = key;
  if (typeof vars.count === "number") {
    // Plural keys are optional: fall back to _other, then to the plain key.
    const form = `${key}_${plural.select(vars.count)}`;
    k = form in dict || form in fallback ? form : `${key}_other` in dict || `${key}_other` in fallback ? `${key}_other` : key;
  }
  const s = dict[k] ?? fallback[k] ?? k;
  return s.replace(/\{(\w+)\}/g, (_, name: string) => (name in vars ? formatVar(vars[name]!) : `{${name}}`));
}

function formatVar(v: string | number): string {
  return typeof v === "number" ? new Intl.NumberFormat(current).format(v) : v;
}
